import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Dropdown, Button, Form, ButtonGroup, Modal, Spinner } from 'react-bootstrap';
import { FaMoneyBillAlt, FaEye, FaFileDownload, FaExclamationTriangle } from 'react-icons/fa';
import axios from 'axios';
import './overtime-report.css';
import { BsPersonWorkspace } from 'react-icons/bs';
 
const rowsPerPageOptions = [10, 25, 50, 100];
const API_BASE_URL = 'https://backend.ems.hiradentallab.com';
 
export default function OverTimeReport() {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [allData, setAllData] = useState([]); // Store all data
  const [filteredData, setFilteredData] = useState([]); // Store filtered data
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [selectedOvertime, setSelectedOvertime] = useState(null);
  const [employeeName, setEmployeeName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const username = localStorage.getItem('name');
 
  // Fetch all data initially without filtering
  const fetchOvertimeData = useCallback(async (userId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/attendance/overtime/${userId}`);
     
      if (response.data.success) {
        setAllData(response.data.data);
        setFilteredData(response.data.data); // Initialize filteredData with all data
        setTotalRecords(response.data.total || response.data.data.length);
      } else {
        setError(response.data.message || 'Error fetching overtime data');
      }
    } catch (error) {
      console.error('API Error:', error);
      setError(error.response?.data?.message || 'Failed to fetch overtime data');
    } finally {
      setLoading(false);
    }
  }, []);
 
  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const userName = localStorage.getItem('userName') || 'Employee';
    setEmployeeName(userName);
    fetchOvertimeData(userId);
  }, [fetchOvertimeData]);
 
  // Filter data based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredData(allData);
      setTotalRecords(allData.length);
      setCurrentPage(1);
      return;
    }
 
    const filtered = allData.filter(item => {
      const formattedDate = formatDate(item.date).toLowerCase();
      return formattedDate.includes(searchTerm.toLowerCase());
    });
 
    setFilteredData(filtered);
    setTotalRecords(filtered.length);
    setCurrentPage(1); // Reset to first page when filtering
  }, [searchTerm, allData]);
 
  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= Math.ceil(totalRecords / rowsPerPage)) {
      setCurrentPage(pageNumber);
    }
  };
 
  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };
 
  const pageRange = () => {
    const totalPages = Math.ceil(totalRecords / rowsPerPage);
    const pages = [];
    const maxVisiblePages = 3;
   
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(currentPage - 1, 1);
      let endPage = Math.min(startPage + maxVisiblePages - 1, totalPages);
 
      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(endPage - maxVisiblePages + 1, 1);
      }
 
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    return pages;
  };
 
  const getDocumentUrl = (docPath) => {
    if (!docPath) return null;
    if (docPath.startsWith('http://') || docPath.startsWith('https://')) {
      return docPath;
    }
    return `${API_BASE_URL}/uploads/${docPath}`;
  };
 
  const viewOvertimeForm = (overtime) => {
    setSelectedOvertime(overtime);
    setShowModal(true);
  };
 
  const downloadOvertimeForm = async (overtime) => {
    const docUrl = getDocumentUrl(overtime.overtimeDocs);
    if (!docUrl) {
      alert('No document available for this overtime record');
      return;
    }
 
    try {
      const response = await fetch(docUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
     
      // Extract filename from URL or use default
      const filename = docUrl.split('/').pop() ||
        `overtime-form-${new Date(overtime.date).toISOString().split('T')[0]}${
          isImageFile(overtime.overtimeDocs) ? '.png' : '.pdf'
        }`;
     
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
     
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Download error:', error);
      // Fallback to opening in new tab if download fails
      window.open(docUrl, '_blank');
    }
  };
 
  const calculateTotalOvertimeHours = () => {
    return filteredData.reduce((total, item) => total + (item.extraMinutes || 0), 0);
  };
 
  const formatMinutesToHours = (minutes) => {
    if (!minutes) return '0h 0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };
 
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };
 
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const time = new Date(timeString);
    return isNaN(time.getTime()) ? 'Invalid Time' : time.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };
 
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };
 
  const handleSearchSubmit = (e) => {
    e.preventDefault();
  };
 
  const isImageFile = (filename) => {
    if (!filename) return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    const ext = filename.substring(filename.lastIndexOf('.')).toLowerCase();
    return imageExtensions.includes(ext);
  };
 
  // Get current page data
  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredData.slice(startIndex, endIndex);
  };
 
  return (
    <div className=' overtime-report-container'>
      <div className="content-wrapper">
        <h1 className="title-header">
          <FaMoneyBillAlt className="icon-spacing" /> My Overtime Report
        </h1>
        <hr className="header-divider" />
       <h1 className="work-reports-subtitle">
              <BsPersonWorkspace className="work-reports-icon" />
              {username ? `${username}` : 'Welcome, Guest!'}
            </h1>
        <hr className="header-divider" />
        <div className='table-container'>
          <div className='filter-container'>
            <div className="filter-left">
              <Dropdown as={ButtonGroup}>
                <Button variant="primary">Show {rowsPerPage} Entries</Button>
                <Dropdown.Toggle split variant="primary" id="dropdown-split-basic" />
                <Dropdown.Menu>
                  {rowsPerPageOptions.map(option => (
                    <Dropdown.Item
                      key={option}
                      onClick={() => handleRowsPerPageChange(option)}
                      active={rowsPerPage === option}
                    >
                      {option}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <div className="filter-right">
              <Form className="d-flex" onSubmit={handleSearchSubmit}>
                <div className="search-input-group">
                  <Form.Control
                    type="search"
                    placeholder="Search by date (MM/DD/YYYY)"
                    aria-label="Search"
                    className="search-input"
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                </div>
              </Form>
            </div>
          </div>
 
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
 
          <div className="table-responsive">
            <table className="table table-striped custom-table">
              <thead className="overtime-table-header">
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th>Overtime</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className='text-center'>
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    </td>
                  </tr>
                ) : getCurrentPageData().length ? (
                  getCurrentPageData().map((item, index) => (
                    <tr key={item._id || index}>
                      <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                      <td>{formatDate(item.date)}</td>
                      <td>{formatTime(item.checkIn)}</td>
                      <td>{formatTime(item.checkOut)}</td>
                      <td>{formatMinutesToHours(item.extraMinutes)}</td>
                      <td>
                        <ButtonGroup>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => viewOvertimeForm(item)}
                            className="action-btn"
                            title="View Form"
                          >
                            <FaEye />
                          </Button>
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => downloadOvertimeForm(item)}
                            className="action-btn"
                            disabled={!item.overtimeDocs}
                            title={!item.overtimeDocs ? "No document available" : "Download Form"}
                          >
                            <FaFileDownload />
                          </Button>
                        </ButtonGroup>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className='text-center no-records'>
                      {searchTerm ? 'No matching records found' : 'No overtime records available'}
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className='overtime-table-footer'>
                 <td className="text-center fw-bold">Total Overtime:</td>
                  <td colSpan="3" className="text-end fw-bold"></td>
                  <td className="fw-bold">{formatMinutesToHours(calculateTotalOvertimeHours())}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
 
            {totalRecords > 0 && (
              <div className="pagination-container">
                <Row>
                  <Col xs={12} md={6} className="pagination-info">
                    Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, totalRecords)} of {totalRecords} Entries
                  </Col>
                  <Col xs={12} md={6} className="pagination-controls">
                    <nav>
                      <ul className="pagination justify-content-end">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            aria-label="Previous"
                          >
                          Previous
                          </button>
                        </li>
                        {pageRange().map(pageNumber => (
                          <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pageNumber)}
                            >
                              {pageNumber}
                            </button>
                          </li>
                        ))}
                        <li className={`page-item ${currentPage === Math.ceil(totalRecords / rowsPerPage) ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === Math.ceil(totalRecords / rowsPerPage)}
                            aria-label="Next"
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </Col>
                </Row>
              </div>
            )}
          </div>
        </div>
      </div>
 
      {/* Overtime Form Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title>
            Overtime Form - {selectedOvertime && formatDate(selectedOvertime.date)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body-custom">
          {selectedOvertime?.overtimeDocs ? (
            <div className="document-viewer">
              {isImageFile(selectedOvertime.overtimeDocs) ? (
                <img
                  src={getDocumentUrl(selectedOvertime.overtimeDocs)}
                  alt={`Overtime Form - ${formatDate(selectedOvertime.date)}`}
                  className="document-image"
                />
              ) : (
                <iframe
                  src={getDocumentUrl(selectedOvertime.overtimeDocs)}
                  title={`Overtime Form - ${formatDate(selectedOvertime.date)}`}
                  className="document-iframe"
                />
              )}
              <div className="document-actions mt-3 text-center">
                <Button
                  variant="primary"
                  onClick={() => downloadOvertimeForm(selectedOvertime)}
                  className="download-btn"
                >
                  <FaFileDownload /> Download Form
                </Button>
              </div>
            </div>
          ) : (
            <div className="no-document text-center py-4">
              <div className="no-document-icon mb-3">
                <FaExclamationTriangle size={48} className="text-danger" />
              </div>
              <h4 className="text-danger mb-3">Overtime Form Not Uploaded!</h4>
              <div className="no-document-message p-3 bg-light rounded">
                <p className="mb-2">No form has been uploaded for this overtime record.</p>
                <p className="mb-0">Please contact your HR department to resolve this issue.</p>
              </div>
              <div className="no-document-details mt-3">
                <p className="text-muted small">
                  <strong>Date:</strong> {formatDate(selectedOvertime?.date)} |
                  <strong> Overtime:</strong> {formatMinutesToHours(selectedOvertime?.extraMinutes)}
                </p>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="modal-footer-custom">
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
} 